/** Partite (inserimento game), classifiche dei gironi, fase finale e classifica finale. */
import { useContext, useEffect, useState } from "react";
import { Check, CloudUpload, Medal, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { PHASE_LABEL, type FinalRow, type GaraState, type Match, type StandingRow, type Team } from "@/lib/gare";
import { PendingCtx } from "./offline";
import { Stepper } from "./ui";

const fmtPts = (p: number) => (Number.isInteger(p) ? String(p) : p.toFixed(1).replace(".", ","));

export function MatchCard({ m, teams, onSave, readOnly, groupTag }: {
  m: Match;
  teams: Record<number, Team>;
  onSave: (m: Match, a: number | null, b: number | null) => Promise<void>;
  readOnly?: boolean;
  groupTag?: boolean;
}) {
  const pend = useContext(PendingCtx)[m.id];
  const [a, setA] = useState((pend ? pend.games_a : m.games_a) ?? 0);
  const [b, setB] = useState((pend ? pend.games_b : m.games_b) ?? 0);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!dirty) {
      setA((pend ? pend.games_a : m.games_a) ?? 0);
      setB((pend ? pend.games_b : m.games_b) ?? 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.version, m.games_a, m.games_b, pend?.ts]);

  const la = m.label_a ?? (m.team_a ? teams[m.team_a]?.label : undefined);
  const lb = m.label_b ?? (m.team_b ? teams[m.team_b]?.label : undefined);
  const done = m.status === "done";
  const ready = !!(la && lb);
  const save = async (va: number | null, vb: number | null) => {
    setBusy(true);
    try {
      await onSave(m, va, vb);
      setDirty(false);
    } finally {
      setBusy(false);
    }
  };
  const where = [m.phase === "group" ? (groupTag ? `Girone ${m.group_label}` : null)
    : m.phase === "round" ? null : PHASE_LABEL[m.phase],
    m.round_no ? `Turno ${m.round_no}` : null, m.court ? `Campo ${m.court}` : null].filter(Boolean).join(" · ");
  const winA = done && (m.games_a ?? 0) > (m.games_b ?? 0);
  const winB = done && (m.games_b ?? 0) > (m.games_a ?? 0);

  return (
    <div className={cn("rounded-xl border bg-white p-3 shadow-sm", done ? "border-moving-200" : "border-slate-200")}>
      <div className="mb-2 flex items-center justify-between gap-2 text-xs">
        <span className="font-medium text-slate-600">{where}</span>
        {pend ? (
          <span className="flex shrink-0 items-center gap-1 font-medium text-amber-700"><CloudUpload className="h-3.5 w-3.5" />in attesa di invio</span>
        ) : done ? (
          <span className="flex items-center gap-1 text-moving-700"><Check className="h-3.5 w-3.5" />
            {m.updated_by ? `salvato da ${m.updated_by}` : "giocata"}</span>
        ) : <span className="shrink-0 text-slate-400">{m.scoring === "set" ? "set a 6, tie-break 6-6" : m.scoring === "set_time" ? "set ai 6 a tempo" : "game a tempo"}</span>}
      </div>
      {!ready ? (
        <p className="py-3 text-center text-sm text-slate-500">Squadre da definire (dopo i risultati precedenti)</p>
      ) : (
        <div className="space-y-2">
          {([[la, a, setA, winA], [lb, b, setB, winB]] as const).map(([t, v, set, win], i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className={cn("min-w-0 flex-1 text-sm leading-tight", win ? "font-bold text-slate-900" : "text-slate-700")}>
                {t}
              </span>
              {readOnly ? (
                <span className="w-10 text-right text-xl font-bold tabular-nums">{done ? (i ? m.games_b : m.games_a) : "–"}</span>
              ) : (
                <Stepper label={t || ""} value={v} onChange={(n) => { set(n); setDirty(true); }} />
              )}
            </div>
          ))}
        </div>
      )}
      {!readOnly && ready ? (
        <div className="mt-3 flex gap-2">
          <Button className="flex-1" size="lg" loading={busy} disabled={!dirty}
            onClick={() => save(a, b)}>
            {pend && !dirty ? "In attesa di invio" : done ? (dirty ? "Correggi risultato" : "Salvato") : "Salva risultato"}
          </Button>
          {done ? (
            <Button variant="outline" size="lg" disabled={busy} onClick={() => save(null, null)}>Cancella</Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function MatchList({ state, onSave, readOnly }: {
  state: GaraState;
  onSave: (m: Match, a: number | null, b: number | null) => Promise<void>;
  readOnly?: boolean;
}) {
  const [filter, setFilter] = useState<"todo" | "all">("todo");
  const teams = Object.fromEntries(state.teams.map((t) => [t.id, t]));
  const ms = state.matches.filter((m) => filter === "all" || m.status !== "done");
  const rounds = Array.from(new Set(ms.map((m) => m.round_no ?? 0))).sort((x, y) => x - y);
  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {(["todo", "all"] as const).map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)}
            className={cn("rounded-full px-3 py-1.5 text-sm font-medium",
              filter === f ? "bg-moving-500 text-white" : "bg-slate-100 text-slate-600")}>
            {f === "todo" ? `Da giocare (${state.matches.filter((m) => m.status !== "done").length})` : `Tutte (${state.matches.length})`}
          </button>
        ))}
      </div>
      {!ms.length ? <p className="rounded-xl bg-moving-50 p-4 text-sm text-moving-800">Nessuna partita da giocare.</p> : null}
      {rounds.map((r) => (
        <div key={r}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Turno {r}</h3>
          <div className="grid gap-3 md:grid-cols-2">
            {ms.filter((m) => (m.round_no ?? 0) === r).map((m) => (
              <MatchCard key={m.id} m={m} teams={teams} onSave={onSave} readOnly={readOnly}
                groupTag={Object.keys(state.groups).length > 1} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function GroupTable({ title, rows, qualifiers, nameHeader = "Coppia" }: {
  title: string; rows: StandingRow[]; qualifiers: number; nameHeader?: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-3 py-2 text-sm font-semibold text-slate-900">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="px-2 py-1.5 text-left">#</th>
              <th className="px-2 py-1.5 text-left">{nameHeader}</th>
              <th className="hidden px-1.5 py-1.5 sm:table-cell" title="Partite giocate">G</th>
              <th className="px-1.5 py-1.5" title="Vinte">V</th>
              <th className="hidden px-1.5 py-1.5 sm:table-cell" title="Pareggiate">N</th>
              <th className="hidden px-1.5 py-1.5 sm:table-cell" title="Perse">P</th>
              <th className="px-1.5 py-1.5" title="Differenza game">DG</th>
              <th className="hidden px-1.5 py-1.5 sm:table-cell" title="Game fatti">GF</th>
              <th className="px-2 py-1.5" title="Punti: vittoria 1, pareggio ½">Pt</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.team_id ?? r.player_id} className={cn("border-t border-slate-100", r.rank <= qualifiers && "bg-moving-50/60")}>
                <td className="px-2 py-1.5 font-semibold text-slate-500">{r.rank}</td>
                <td className="px-2 py-1.5 font-medium text-slate-900">{r.label}</td>
                <td className="hidden px-1.5 py-1.5 text-center tabular-nums sm:table-cell">{r.played}</td>
                <td className="px-1.5 py-1.5 text-center tabular-nums">{r.won}</td>
                <td className="hidden px-1.5 py-1.5 text-center tabular-nums sm:table-cell">{r.drawn}</td>
                <td className="hidden px-1.5 py-1.5 text-center tabular-nums sm:table-cell">{r.lost}</td>
                <td className="px-1.5 py-1.5 text-center tabular-nums">{r.diff > 0 ? `+${r.diff}` : r.diff}</td>
                <td className="hidden px-1.5 py-1.5 text-center tabular-nums sm:table-cell">{r.gf}</td>
                <td className="px-2 py-1.5 text-center font-bold tabular-nums">{fmtPts(r.points)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Knockout({ state, onSave, onTeams, readOnly }: {
  state: GaraState;
  onSave: (m: Match, a: number | null, b: number | null) => Promise<void>;
  onTeams: (m: Match, b: { team_a?: number; team_b?: number; auto?: boolean }) => void;
  readOnly?: boolean;
}) {
  const teams = Object.fromEntries(state.teams.map((t) => [t.id, t]));
  const ko = state.matches.filter((m) => m.phase !== "group");
  const groupsDone = state.matches.filter((m) => m.phase === "group").every((m) => m.status === "done");
  if (!ko.length) {
    return (
      <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
        {groupsDone ? "Fase finale in preparazione…" : "La fase finale si crea da sola quando tutte le partite dei gironi hanno un risultato."}
      </p>
    );
  }
  const order: Match["phase"][] = ["semi", "final", "third", "gold_semi", "gold_final", "gold_third"];
  const shown = order.filter((ph) => ko.some((m) => m.phase === ph));
  const isGold = (ph: string) => ph.startsWith("gold");
  return (
    <div className="space-y-4">
      {order.map((ph) => {
        const list = ko.filter((m) => m.phase === ph);
        if (!list.length) return null;
        return (
          <div key={ph}>
            {shown.find((p) => isGold(p) === isGold(ph)) === ph ? (
              <h2 className={cn("mb-3 rounded-lg px-3 py-2 text-sm font-bold uppercase tracking-wide",
                isGold(ph) ? "bg-amber-50 text-amber-800" : "bg-sky-50 text-court-700")}>
                {isGold(ph) ? "Fase Gold · 5°-8° posto" : "Fase Diamond · 1°-4° posto"}
              </h2>
            ) : null}
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {ph === "semi" || ph === "gold_semi" ? "Semifinali" : PHASE_LABEL[ph]}
            </h3>
            <div className="grid gap-3 md:grid-cols-2">
              {list.map((m) => (
                <div key={m.id} className="space-y-1.5">
                  <MatchCard m={m} teams={teams} onSave={onSave} readOnly={readOnly} />
                  {!readOnly && m.status !== "done" ? (
                    <details className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <summary className="cursor-pointer">Cambia squadre a mano{m.locked ? " (modificata a mano)" : ""}</summary>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {(["team_a", "team_b"] as const).map((k) => (
                          <select key={k} value={m[k] ?? ""} aria-label={k === "team_a" ? "Prima squadra" : "Seconda squadra"}
                            onChange={(e) => onTeams(m, {
                              team_a: k === "team_a" ? Number(e.target.value) : (m.team_a ?? undefined),
                              team_b: k === "team_b" ? Number(e.target.value) : (m.team_b ?? undefined),
                            })}
                            className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
                            <option value="" disabled>Scegli…</option>
                            {state.teams.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                        ))}
                      </div>
                      {m.locked ? (
                        <button type="button" onClick={() => onTeams(m, { auto: true })}
                          className="mt-2 flex items-center gap-1 text-moving-700"><RefreshCw className="h-3.5 w-3.5" /> Torna agli abbinamenti automatici</button>
                      ) : null}
                    </details>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function FinalTable({ rows, provisional, roadTo, emptyText }: {
  rows: FinalRow[]; provisional: boolean; roadTo: boolean; emptyText?: string;
}) {
  if (!rows.length) {
    return <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
      {emptyText || "La classifica finale compare quando tutte le finali, Diamond e Gold, hanno un risultato."}</p>;
  }
  const medal = ["text-amber-500", "text-slate-400", "text-orange-700"];
  return (
    <div className="space-y-2">
      {provisional ? <p className="text-xs text-amber-700">Provvisoria: diventa definitiva quando chiudi il torneo.</p> : null}
      <ol className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {rows.map((r) => (
          <li key={`${r.position}-${r.team_id}`} className="flex items-center gap-3 border-t border-slate-100 px-3 py-2.5 first:border-t-0">
            <span className="w-8 text-center text-lg font-bold text-slate-700">
              {r.position <= 3 ? <Medal className={cn("mx-auto h-6 w-6", medal[r.position - 1])} aria-label={`${r.position}°`} /> : `${r.position}°`}
            </span>
            <span className="flex-1 text-sm font-medium text-slate-900">{r.label}</span>
            {roadTo ? <span className="text-sm font-semibold tabular-nums text-moving-700">{r.points} pt</span> : null}
          </li>
        ))}
      </ol>
      {roadTo ? <p className="text-xs text-slate-500">Punti Road To a testa (formula in prova, può cambiare).</p> : null}
    </div>
  );
}
